(() => {
  const selectors = {
    root: "[data-aw-cc]",
    viewport: "[data-aw-cc-viewport]",
    track: "[data-aw-cc-track]",
    prev: "[data-aw-cc-prev]",
    next: "[data-aw-cc-next]",
    dots: "[data-aw-cc-dots]",
    controls: "[data-aw-cc-controls]"
  };

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  function getSlideWidth(track) {
    const slide = track.querySelector(".aw-cc__slide");
    if (!slide) return 0;

    const rect = slide.getBoundingClientRect();
    const style = window.getComputedStyle(track);
    const gap = parseFloat(style.columnGap || style.gap || "0") || 0;

    return rect.width + gap;
  }

  function getPages(track, viewport) {
    const slideW = getSlideWidth(track);
    if (!slideW) return { pages: 1, pageIndex: 0, slideW: 0 };

    const maxScroll = track.scrollWidth - viewport.clientWidth;
    const pages = Math.max(1, Math.round(maxScroll / slideW) + 1);
    const pageIndex = clamp(Math.round(track.scrollLeft / slideW), 0, pages - 1);

    return { pages, pageIndex, slideW };
  }

  function scrollToPage(track, viewport, pageIndex) {
    const { pages, slideW } = getPages(track, viewport);
    const target = clamp(pageIndex, 0, pages - 1) * slideW;

    track.scrollTo({ left: target, behavior: "smooth" });
  }

  function buildDots(dotsEl, pages, clickable) {
    dotsEl.innerHTML = "";
    for (let i = 0; i < pages; i++) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "aw-cc__dot";
      btn.setAttribute("aria-label", `Go to slide ${i + 1}`);
      btn.dataset.index = String(i);
      btn.disabled = !clickable;

      const bullet = document.createElement("i");
      btn.appendChild(bullet);

      dotsEl.appendChild(btn);
    }
  }

  function setActiveDot(dotsEl, index) {
    const dots = [...dotsEl.querySelectorAll(".aw-cc__dot")];
    dots.forEach((d, i) => d.classList.toggle("is-active", i === index));
  }

  function updateArrows(prevBtn, nextBtn, pageIndex, pages) {
    const atStart = pageIndex <= 0;
    const atEnd = pageIndex >= pages - 1;

    if (prevBtn) prevBtn.disabled = atStart;
    if (nextBtn) nextBtn.disabled = atEnd;
  }

  function setup(root) {
    const viewport = root.querySelector(selectors.viewport);
    const track = root.querySelector(selectors.track);
    const prevBtn = root.querySelector(selectors.prev);
    const nextBtn = root.querySelector(selectors.next);
    const dotsEl = root.querySelector(selectors.dots);

    if (!viewport || !track) return;

    // =========================
    // DATA / SETTINGS
    // =========================
    const controlsMode = root.dataset.controls || "auto"; // auto | always
    const dotsClickable = root.dataset.dotsClickable === "true";

    const autoplayEnabled = root.dataset.autoplay === "true";
    const autoplayInterval = parseInt(root.dataset.autoplayInterval || "4500", 10);
    const pauseOnHover = root.dataset.pauseOnHover === "true";
    const loop = root.dataset.loop === "true";
    const respectRM = root.dataset.reducedMotion === "true";

    // state
    let autoplayTimer = null;
    let userInteracted = false;

    // Breakpoint usado pelo teu CSS (min-width: 750px)
    const mqDesktop = window.matchMedia("(min-width: 750px)");

    const getBreakpointToggles = () => {
      const isDesktop = mqDesktop.matches;

      const showArrows = (isDesktop ? root.dataset.arrowsDesktop : root.dataset.arrowsMobile) === "true";
      const showDots = (isDesktop ? root.dataset.dotsDesktop : root.dataset.dotsMobile) === "true";

      return { isDesktop, showArrows, showDots };
    };

    // =========================
    // CORE REFRESH
    // =========================
    const refresh = () => {
      const { pages, pageIndex } = getPages(track, viewport);

      // overflow real
      const canScroll = (track.scrollWidth - viewport.clientWidth) > 2;

      // AUTO: se não tem overflow, esconde tudo (arrows + dots)
      if (controlsMode === "auto" && !canScroll) {
        root.classList.add("is-controls-hidden");
        root.classList.add("is-dots-hidden");
        root.classList.add("is-arrows-hidden");
        return;
      }

      // caso contrário, mostra container de dots (o CSS já controla)
      root.classList.remove("is-controls-hidden");

      const { showArrows, showDots } = getBreakpointToggles();

      // aplica toggles por breakpoint
      root.classList.toggle("is-arrows-hidden", !showArrows);
      root.classList.toggle("is-dots-hidden", !showDots);

      // dots
      if (showDots && dotsEl) {
        const currentDots = dotsEl.querySelectorAll(".aw-cc__dot").length;
        if (currentDots !== pages) buildDots(dotsEl, pages, dotsClickable);
        setActiveDot(dotsEl, pageIndex);
      }

      // arrows
      if (showArrows) updateArrows(prevBtn, nextBtn, pageIndex, pages);
    };

    // =========================
    // NAV
    // =========================
    const goPrev = () => {
      userInteracted = true;

      const { pages, pageIndex } = getPages(track, viewport);
      const nextIndex = pageIndex - 1;

      if (nextIndex < 0) {
        if (loop) scrollToPage(track, viewport, pages - 1);
        return;
      }
      scrollToPage(track, viewport, nextIndex);
    };

    const goNext = () => {
      userInteracted = true;

      const { pages, pageIndex } = getPages(track, viewport);
      const nextIndex = pageIndex + 1;

      if (nextIndex > pages - 1) {
        if (loop) scrollToPage(track, viewport, 0);
        return;
      }
      scrollToPage(track, viewport, nextIndex);
    };

    // =========================
    // EVENTS
    // =========================
    if (prevBtn) prevBtn.addEventListener("click", goPrev);
    if (nextBtn) nextBtn.addEventListener("click", goNext);

    // Dots click (respeita toggle atual)
    if (dotsEl && dotsClickable) {
      dotsEl.addEventListener("click", (e) => {
        const btn = e.target.closest(".aw-cc__dot");
        if (!btn) return;

        const { showDots } = getBreakpointToggles();
        if (!showDots) return;

        userInteracted = true;
        const idx = parseInt(btn.dataset.index || "0", 10);
        scrollToPage(track, viewport, idx);
      });
    }

    // scroll -> refresh
    let raf = null;
    track.addEventListener("scroll", () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(refresh);
    });

    // resize -> refresh (debounce)
    let resizeTimer = null;
    window.addEventListener(
      "resize",
      () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(refresh, 120);
      },
      { passive: true }
    );

    // breakpoint change -> refresh
    if (mqDesktop.addEventListener) {
      mqDesktop.addEventListener("change", refresh);
    } else if (mqDesktop.addListener) {
      mqDesktop.addListener(refresh);
    }

    // =========================
    // AUTOPLAY
    // =========================
    const canAutoplay = autoplayEnabled && (!respectRM || !prefersReducedMotion());

    const stopAutoplay = () => {
      if (autoplayTimer) clearInterval(autoplayTimer);
      autoplayTimer = null;
    };

    const startAutoplay = () => {
      if (!canAutoplay) return;
      stopAutoplay();
      autoplayTimer = setInterval(() => {
        goNext();
      }, autoplayInterval);
    };

    if (pauseOnHover) {
      root.addEventListener("mouseenter", stopAutoplay);
      root.addEventListener("mouseleave", startAutoplay);
    }

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    ["pointerdown", "touchstart", "keydown", "wheel"].forEach((evt) => {
      track.addEventListener(
        evt,
        () => {
          userInteracted = true;
          stopAutoplay();
          setTimeout(startAutoplay, 900);
        },
        { passive: true }
      );
    });

    // =========================
    // INITIAL
    // =========================
    refresh();
    setTimeout(refresh, 80);
    window.addEventListener("load", refresh, { passive: true });
    startAutoplay();
  }

  function init() {
    document.querySelectorAll(selectors.root).forEach(setup);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
